/**
 * Operations with Decimals generator (6.PAFR.3.7).
 *
 * Positive decimals (to thousandths) across all four operations. Each operand
 * is built as an exact Rational from an integer numerator over a power of ten,
 * so arithmetic is exact - no floating-point drift. The answer is a
 * DecimalAnswer carrying the exact value; grading accepts either the decimal or
 * an equivalent fraction, and the displayed place count comes from the operands.
 *
 * Families:
 *   F1: a + b
 *   F2: a - b   (arranged so the difference is positive)
 *   F3: a * b
 *   F4: a / b   (built from a product so the quotient terminates)
 */

import { rat, type Rational } from "../engine/rational";
import { Rng } from "../engine/rng";
import { getTopic } from "../curriculum/curriculum";
import { rationalToDecimal } from "../engine/integer";
import type {
  Difficulty,
  GenerateContext,
  Generator,
  Question,
  QuestionSignature,
} from "./types";

const TOPIC = "decimal-operations" as const;
const VERSION = "1.0.0";

type Family = "F1" | "F2" | "F3" | "F4";

interface Cfg {
  readonly families: readonly Family[];
  /** Number of decimal places for additive operands. */
  readonly places: number;
  /** Whole-number magnitude of operands (integer part range). */
  readonly wholeMax: number;
}

const DIFF: Record<Difficulty, Cfg> = {
  easy: { families: ["F1", "F2"], places: 1, wholeMax: 9 },
  standard: { families: ["F1", "F2", "F3", "F4"], places: 2, wholeMax: 20 },
  hard: { families: ["F1", "F2", "F3", "F4"], places: 3, wholeMax: 50 },
  challenge: { families: ["F2", "F3", "F4"], places: 3, wholeMax: 99 },
};

/** A positive decimal with `places` digits after the point, as an exact Rational. */
function makeDecimal(rng: Rng, wholeMax: number, places: number): Rational {
  const den = 10 ** places;
  const whole = rng.int(0, wholeMax);
  const frac = rng.int(0, den - 1);
  const num = whole * den + frac;
  // Avoid a plain zero operand.
  const safeNum = num === 0 ? 1 : num;
  return rat(BigInt(safeNum), BigInt(den));
}

/** Count of decimal places needed to display a value exactly (capped). */
function placesOf(value: Rational, cap = 4): number {
  for (let p = 0; p <= cap; p++) {
    const scaled = value.mul(rat(10 ** p));
    if (scaled.isInteger()) return p;
  }
  return cap;
}

export class DecimalOperationsGenerator implements Generator {
  readonly topic = TOPIC;
  readonly version = VERSION;
  readonly supportedDifficulties: readonly Difficulty[] = [
    "easy",
    "standard",
    "hard",
    "challenge",
  ];

  generate(ctx: GenerateContext): Question {
    const topic = getTopic(TOPIC);
    for (let attempt = 0; attempt < 100; attempt++) {
      const rng = new Rng(`${ctx.seed}#${attempt}`);
      const cfg = DIFF[ctx.difficulty];
      const family = rng.pick(cfg.families);

      let a = makeDecimal(rng, cfg.wholeMax, cfg.places);
      let b = makeDecimal(rng, cfg.wholeMax, cfg.places);
      let answer: Rational;
      let opText: string;
      let steps: { description: string; latex: string }[];

      switch (family) {
        case "F1": {
          answer = a.add(b);
          opText = `${dec(a)} + ${dec(b)}`;
          steps = [
            {
              description: "Line up the decimal points, then add.",
              latex: `${dec(a)} + ${dec(b)} = ${dec(answer)}`,
            },
          ];
          break;
        }
        case "F2": {
          if (a.lt(b)) [a, b] = [b, a];
          answer = a.sub(b);
          if (answer.isZero()) continue;
          opText = `${dec(a)} - ${dec(b)}`;
          steps = [
            {
              description: "Line up the decimal points, then subtract.",
              latex: `${dec(a)} - ${dec(b)} = ${dec(answer)}`,
            },
          ];
          break;
        }
        case "F3": {
          // Keep factors modest so the product stays readable.
          a = makeDecimal(rng, Math.min(cfg.wholeMax, 12), Math.min(cfg.places, 2));
          b = makeDecimal(rng, Math.min(cfg.wholeMax, 12), 1);
          answer = a.mul(b);
          opText = `${dec(a)} \\times ${dec(b)}`;
          steps = [
            {
              description:
                "Multiply as whole numbers, then place the decimal point using the total number of decimal places.",
              latex: `${dec(a)} \\times ${dec(b)} = ${dec(answer)}`,
            },
          ];
          break;
        }
        case "F4": {
          // Build from a product so the quotient terminates cleanly.
          const quotient = makeDecimal(rng, Math.min(cfg.wholeMax, 12), 1);
          const divisor = makeDecimal(rng, Math.min(cfg.wholeMax, 12), 1);
          const dividend = quotient.mul(divisor);
          a = dividend;
          b = divisor;
          answer = quotient;
          opText = `${dec(a)} \\div ${dec(b)}`;
          steps = [
            {
              description:
                "Shift the decimal point in both numbers to make the divisor whole, then divide.",
              latex: `${dec(a)} \\div ${dec(b)} = ${dec(answer)}`,
            },
          ];
          break;
        }
      }

      const places = placesOf(answer);

      return {
        id: `${TOPIC}-${ctx.seed}-${family}`,
        generator: TOPIC,
        generatorVersion: VERSION,
        seed: ctx.seed,
        unit: topic.unit,
        topic: TOPIC,
        family,
        difficulty: ctx.difficulty,
        prompt: {
          text: `Compute: ${rationalToDecimal(a, placesOf(a))} ${opWord(family)} ${rationalToDecimal(b, placesOf(b))}`,
          latex: `${opText} = \\;?`,
        },
        answerType: "decimal",
        answer: { kind: "decimal", value: answer, places },
        steps,
        hints: [
          { level: 1, text: "Keep the place values lined up as you work." },
          {
            level: 2,
            text: "For multiplication, count total decimal places; for division, make the divisor whole first.",
          },
        ],
        subskills: topic.subskills,
      };
    }
    throw new Error(`${TOPIC}: failed after 100 attempts (${ctx.seed}, ${ctx.difficulty})`);
  }

  signature(q: Question): QuestionSignature {
    const structure = q.answer.kind === "decimal" ? q.answer.value.key() : q.answer.kind;
    return { generator: TOPIC, family: q.family, difficulty: q.difficulty, structure };
  }
}

/** Render a Rational as a decimal string for LaTeX (exact place count). */
function dec(value: Rational): string {
  return rationalToDecimal(value, placesOfLatex(value));
}
function placesOfLatex(value: Rational): number {
  for (let p = 0; p <= 4; p++) {
    if (value.mul(rat(10 ** p)).isInteger()) return p;
  }
  return 4;
}

function opWord(family: Family): string {
  switch (family) {
    case "F1":
      return "+";
    case "F2":
      return "-";
    case "F3":
      return "*";
    case "F4":
      return "/";
  }
}

export const decimalOperations = new DecimalOperationsGenerator();
